/**
 * Навык в адресе ленты вакансий (ТЗ 0070).
 *
 * Паспорт навыка на карте обещает «13 вакансий в нашей ленте» и ведёт сюда
 * ссылкой `/career/jobs?skill=docker`. До этого фильтры ленты жили только в
 * `useState`: адрес переживал редирект со старого `/career/vacancies`, но никто
 * его не читал — параметры молча пропадали.
 *
 * 🔴 Слаг, а не числовой id. У паспорта слаг есть сразу, а id навыка приезжает
 * только с ответом сервера; ссылка на `?skill=28` вдобавок нечитаема и не
 * переносима между стендами.
 *
 * ⚠️ Чужие параметры адреса (utm-метки, `sort`, фильтры ленты) при снятии
 * навыка обязаны выжить — тот же инвариант, что у `skillMapParams` на карте.
 */
export const VACANCY_SKILL_PARAM = 'skill';

/**
 * Та же форма слага, что валидирует карта навыков. Копия константы, а не
 * импорт из чужого экрана: связывать ленту вакансий с моделью карты ради
 * одного регулярного выражения — цена выше пользы.
 */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

/** Слаг навыка из адреса, либо `null`, если его нет или он битый. */
export const readVacancySkillSlug = (params: URLSearchParams): string | null => {
  const raw = (params.get(VACANCY_SKILL_PARAM) || '').trim().toLowerCase();
  return SLUG_RE.test(raw) ? raw : null;
};

/** Тот же адрес без навыка. Остальные параметры сохраняются как есть. */
export const dropVacancySkill = (params: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(VACANCY_SKILL_PARAM);
  return next;
};

/**
 * Режим подбора и фильтры ленты — в адресе (ТЗ 0072, шаг 5).
 *
 * Аудит: четыре пересекающихся способа персонализации (авто-направление и
 * уровень, «цель / профиль», сортировка, сохранённые), и ни один из них не
 * переживал возврат из вакансии. Теперь РЕЖИМ и ФИЛЬТРЫ — разные сущности:
 *
 * * `mode` — чем подбирать: целью, профилем или ничем («Wszystkie oferty»);
 * * фильтры — то, что человек выбрал сам; «Wszystkie oferty» их не снимает,
 *   «Wyczyść filtry» не трогает режим.
 *
 * Ничего не подставляется молча: прежнее «открываем ленту суженной по
 * направлению и уровню» было фильтром, который человек не выбирал.
 */
export type VacancyMode = 'goal' | 'profile' | 'all';

export const VACANCY_MODES: VacancyMode[] = ['goal', 'profile', 'all'];

/** Режим → значение `matchMode` сервера. */
export const MATCH_MODE_OF: Record<VacancyMode, string> = {
  goal: 'GOAL',
  profile: 'PROFILE',
  all: 'ALL',
};

export interface IVacancyFilters {
  direction: string;
  city: string;
  workMode: string;
  seniority: string;
  employment: string;
  sort: 'relevance' | 'newest';
  search: string;
  saved: boolean;
}

/** Ключ фильтра → параметр адреса. Навык живёт в `VACANCY_SKILL_PARAM`. */
const FILTER_PARAM: Record<keyof IVacancyFilters, string> = {
  direction: 'direction',
  city: 'city',
  workMode: 'work',
  seniority: 'level',
  employment: 'employment',
  sort: 'sort',
  search: 'q',
  saved: 'saved',
};

const MODE_PARAM = 'mode';
/** Открытая карточка — чтобы «назад» с сайта работодателя вернул её же. */
export const VACANCY_OFFER_PARAM = 'offer';

/** По умолчанию — цель, если она выбрана, иначе профиль. */
export const readVacancyMode = (params: URLSearchParams, hasGoal: boolean): VacancyMode => {
  const raw = params.get(MODE_PARAM);
  if (raw === 'all' || raw === 'profile') return raw;
  if (raw === 'goal' && hasGoal) return 'goal';
  return hasGoal ? 'goal' : 'profile';
};

export const readVacancyFilters = (params: URLSearchParams): IVacancyFilters => ({
  direction: params.get(FILTER_PARAM.direction) || '',
  city: params.get(FILTER_PARAM.city) || '',
  workMode: params.get(FILTER_PARAM.workMode) || '',
  seniority: params.get(FILTER_PARAM.seniority) || '',
  employment: params.get(FILTER_PARAM.employment) || '',
  sort: params.get(FILTER_PARAM.sort) === 'newest' ? 'newest' : 'relevance',
  search: params.get(FILTER_PARAM.search) || '',
  saved: params.get(FILTER_PARAM.saved) === '1',
});

/** Тот же адрес с новым режимом. Фильтры и чужие параметры не трогаются. */
export const withVacancyMode = (params: URLSearchParams, mode: VacancyMode): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.set(MODE_PARAM, mode);
  return next;
};

/** Тот же адрес с изменённым фильтром; пустое значение снимает параметр. */
export const withVacancyFilter = <K extends keyof IVacancyFilters>(
  params: URLSearchParams,
  key: K,
  value: IVacancyFilters[K],
): URLSearchParams => {
  const next = new URLSearchParams(params);
  const name = FILTER_PARAM[key];
  const empty = value === '' || value === false || (key === 'sort' && value === 'relevance');
  if (empty) next.delete(name);
  else next.set(name, value === true ? '1' : String(value));
  return next;
};

/**
 * «Wyczyść filtry»: снимает всё, что выбрал человек, включая навык и поиск.
 * Режим, открытая карточка и чужие параметры (utm) остаются.
 */
export const clearVacancyFilters = (params: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(params);
  Object.values(FILTER_PARAM)
    .filter((name) => name !== FILTER_PARAM.sort)
    .forEach((name) => next.delete(name));
  next.delete(VACANCY_SKILL_PARAM);
  return next;
};

/** Активные ограничения — для удаляемых меток. Сортировка ограничением не является. */
export const activeVacancyFilters = (
  filters: IVacancyFilters,
  skillSlug: string | null,
): (keyof IVacancyFilters | 'skill')[] => {
  const keys: (keyof IVacancyFilters | 'skill')[] = [];
  (
    ['direction', 'city', 'workMode', 'seniority', 'employment', 'search', 'saved'] as const
  ).forEach((key) => {
    if (filters[key]) keys.push(key);
  });
  if (skillSlug) keys.push('skill');
  return keys;
};

/** Снять одно ограничение по ключу метки. */
export const withoutVacancyFilter = (
  params: URLSearchParams,
  key: keyof IVacancyFilters | 'skill',
): URLSearchParams => {
  if (key === 'skill') return dropVacancySkill(params);
  const next = new URLSearchParams(params);
  next.delete(FILTER_PARAM[key]);
  return next;
};
